/**
 * 语音 UI 组件集（S2 · 设计稿 V-UI）
 *
 * - ImmersivePanel：沉浸态 01b 左右分栏定稿——左 330px 舞台（168px 肖像+名字+状态+
 *   语音时光环），右侧转写流 + 底部控件（缩小/结束/键盘）；背景压暗；肖像永不被遮挡
 * - FloatingCapsule：悬浮态角落胶囊（形象+声波+计时），页面可操作，对话不中断
 * - VoiceTimeRing：语音时光进度环三档（<80% 无数字 / 80-95% 环变浅悬停显分钟 / >95% 琥珀常显）
 * - ConsentModal：首次告知（V-COMP-01）
 * - QuotaModal：达量弹窗（V-UI-16 情绪优先 + 三出口）
 * - SpotlightOverlay：引导态聚光灯（V-UI-03，同屏仅一个高亮，尊重减弱动态）
 * - 八态动画走 CSS 状态类（.voice-stage[data-anim]），肖像无图时用首字母头像（严禁占位人脸）
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Minimize2, X, Keyboard, PhoneOff, VolumeX, Volume2, Send } from 'lucide-react';
import { useVoice } from './VoiceContext';
import { useAssistant } from '../hooks/useAssistant';
import { useT, useI18n } from '../i18n';

import api from '../api';
import './voice.css';

// ──────────────────────────────────────────────
// 肖像：有图用图，无图首字母（用户定：严禁占位人脸图）
// ──────────────────────────────────────────────
const AVATAR_MOTION_REPLAY_MS = 45000; // 短视频不无限循环，隔一段时间轻动一下即可

function Avatar({ size = 168, className = '', motion = false }) {
  const { activeAssistant } = useAssistant();
  const { motionUrl, portraitUrl } = useVoice();
  const name = activeAssistant?.name || 'AI';
  const src = activeAssistant?.avatar_thumb || null;
  const videoRef = useRef(null);

  // S3：小动作剪辑就绪 → 舞台位静默升级为短视频；播放一次即停，
  // 之后按长间隔重新播放，避免无限循环造成烦躁（V-AVATAR-13）
  useEffect(() => {
    if (!(motion && motionUrl)) return undefined;
    const video = videoRef.current;
    const replay = () => {
      if (!video) return;
      try { video.currentTime = 0; video.play(); } catch (_) { /* 未就绪时忽略 */ }
    };
    const timer = setInterval(replay, AVATAR_MOTION_REPLAY_MS);
    return () => clearInterval(timer);
  }, [motion, motionUrl]);

  if (motion && motionUrl) {
    return <video ref={videoRef} src={motionUrl} className={`voice-avatar voice-avatar-video ${className}`} style={{ width: size, height: size }} autoPlay muted playsInline />;
  }
  // V-AVATAR-04/12：动画未就绪时优先 portraitMaster 半身像，其次头像缩略图
  const imgSrc = portraitUrl || src;
  if (imgSrc) {
    return <img src={imgSrc} alt={name} className={`voice-avatar ${className}`} style={{ width: size, height: size }} draggable={false} />;
  }
  return (
    <div className={`voice-avatar voice-avatar-initial ${className}`} style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

// ──────────────────────────────────────────────
// 语音时光进度环（V-UI-14 三档）
// ──────────────────────────────────────────────
function VoiceTimeRing({ size = 168, children }) {
  const { remainingSec, grantedSec } = useVoice();
  const t = useT();
  const [hover, setHover] = useState(false);

  const ratio = useMemo(() => {
    if (remainingSec === null || remainingSec === undefined || !grantedSec) return null;
    return Math.max(0, Math.min(1, remainingSec / grantedSec));
  }, [remainingSec, grantedSec]);

  // 永久权益/无数据 → 细线中性环
  const tier = ratio === null ? 'none' : ratio > 0.2 ? 'calm' : ratio > 0.05 ? 'warn' : 'critical';
  const stroke = 3;
  const r = (size + 14) / 2;
  const c = 2 * Math.PI * r;
  const minutes = remainingSec !== null ? Math.ceil(remainingSec / 60) : null;

  return (
    <div
      className="voice-ring-wrap"
      style={{ width: size + 14, height: size + 14 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={tier !== 'calm' && tier !== 'none' && minutes !== null ? t('voice.time_left_min', { n: minutes }) : undefined}
    >
      <svg width={size + 14} height={size + 14} className="voice-ring">
        <circle cx={r} cy={r} r={r - stroke} fill="none" stroke="var(--border)" strokeWidth={stroke} opacity={0.5} />
        {ratio !== null && (
          <circle
            cx={r} cy={r} r={r - stroke} fill="none"
            className={`voice-ring-arc tier-${tier}`}
            strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - ratio)}
            transform={`rotate(-90 ${r} ${r})`}
          />
        )}
      </svg>
      <div className="voice-ring-inner">{children}</div>
      {(tier === 'critical' || (tier === 'warn' && hover)) && minutes !== null && (
        <div className="voice-ring-label">{t('voice.time_left_min', { n: minutes })}</div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// 沉浸态（01b 定稿：左舞台右转写）
// ──────────────────────────────────────────────
function ImmersivePanel() {
  const { state, speaking, transcripts, endVoice, setUiMode, keyboardSend, resumeBlocked, resumeConfirm, resumeFromSuspend, billedSec, voiceError } = useVoice();
  const { motionToast, setMotionToast, alertNotice } = useVoice();
  const { activeAssistant } = useAssistant();
  const t = useT();
  const [kbOpen, setKbOpen] = useState(false);
  const [kbText, setKbText] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcripts]);

  // 静默升级情绪提示：4s 自动淡出（V-AVATAR-13）
  useEffect(() => {
    if (!motionToast) return undefined;
    const timer = setTimeout(() => setMotionToast(null), 4000);
    return () => clearTimeout(timer);
  }, [motionToast, setMotionToast]);

  const anim = voiceError ? 'error' : speaking === 'assistant' ? 'speaking' : speaking === 'user' ? 'listening' : state === 'suspended' ? 'suspended' : state === 'connecting' ? 'thinking' : 'idle';
  const mm = Math.floor(billedSec / 60);
  const ss = String(Math.floor(billedSec % 60)).padStart(2, '0');

  return (
    <div className="voice-overlay" role="dialog" aria-label={t('voice.title')}>
      <div className="voice-immersive">
        {/* 左舞台（01b：330px，肖像不被遮挡） */}
        <div className="voice-stage" data-anim={anim}>
          <VoiceTimeRing size={168}>
            <Avatar size={168} motion />
          </VoiceTimeRing>
          {motionToast && <div className="voice-motion-toast">{t('voice.avatar_motion_toast', { name: activeAssistant?.name || 'AI' })}</div>}
          {alertNotice && <div className="voice-motion-toast voice-alert-toast">{alertNotice.text}</div>}
          <div className="voice-stage-name">{activeAssistant?.name || 'AI'}</div>
          <div className="voice-stage-status">
            {state === 'connecting' && t('voice.state_connecting')}
            {state === 'active' && (speaking === 'user' ? t('voice.state_listening') : speaking === 'assistant' ? t('voice.state_speaking') : t('voice.state_active'))}
            {state === 'suspended' && t('voice.state_suspended')}
          </div>
          <div className="voice-stage-timer">{mm}:{ss}</div>
        </div>

        {/* 右转写列 */}
        <div className="voice-main">
          <div className="voice-transcripts" ref={scrollRef}>
            {transcripts.length === 0 && (
              <div className="voice-empty">{t('voice.empty_hint')}</div>
            )}
            {transcripts.map((m, i) => m.role === 'citation' ? (
              <div key={i} className="voice-msg voice-msg-citation">
                📚 {t('voice.citation_kb', { n: m.citation.count })}
              </div>
            ) : (
              <div key={i} className={`voice-msg ${m.role === 'user' ? 'voice-msg-user' : 'voice-msg-assistant'}`}>
                {m.text}
              </div>
            ))}
          </div>

          {/* 底部控件：缩小 / 键盘 / 结束（V-UI-06 每个语音指令都有可见按钮） */}
          <div className="voice-controls">
            <button className="voice-btn" onClick={() => setUiMode('capsule')} title={t('voice.minimize')} aria-label={t('voice.minimize')}>
              <Minimize2 size={18} />
            </button>
            <button className="voice-btn" onClick={() => setUiMode('hidden')} title={t('voice.hide_panel')} aria-label={t('voice.hide_panel')}>
              <X size={18} />
            </button>
            <button className="voice-btn" onClick={() => setKbOpen(v => !v)} title={t('voice.keyboard')} aria-label={t('voice.keyboard')}>
              <Keyboard size={18} />
            </button>
            {state === 'suspended' ? (
              resumeBlocked ? (
                <button className="voice-btn voice-btn-resume" onClick={resumeConfirm}>{t('voice.resume_click')}</button>
              ) : (
                <button className="voice-btn voice-btn-resume" onClick={resumeFromSuspend}>{t('voice.resume')}</button>
              )
            ) : (
              <div className="voice-btn-spacer" />
            )}
            <button className="voice-btn voice-btn-end" onClick={endVoice} title={t('voice.end')} aria-label={t('voice.end')}>
              <PhoneOff size={18} />
            </button>
          </div>

          {kbOpen && (
            <div className="voice-keyboard-row">
              <input
                autoFocus
                value={kbText}
                onChange={(e) => setKbText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && kbText.trim()) { keyboardSend(kbText); setKbText(''); } }}
                placeholder={t('voice.keyboard_placeholder')}
                className="voice-keyboard-input"
              />
              <button className="voice-btn" onClick={() => { if (kbText.trim()) { keyboardSend(kbText); setKbText(''); } }} aria-label={t('voice.send')}>
                <Send size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 悬浮态胶囊（V-UI-02：切页面/操作不中断）
// ──────────────────────────────────────────────
function FloatingCapsule() {
  const { state, speaking, setUiMode, endVoice, resumeBlocked, resumeConfirm, resumeFromSuspend, billedSec, voiceError } = useVoice();
  const t = useT();
  const anim = voiceError ? 'error' : speaking === 'assistant' ? 'speaking' : speaking === 'user' ? 'listening' : state === 'suspended' ? 'suspended' : 'idle';
  const mm = Math.floor(billedSec / 60);
  const ss = String(Math.floor(billedSec % 60)).padStart(2, '0');

  return (
    <div className="voice-capsule" data-anim={anim} role="button" tabIndex={0}
      onClick={() => state === 'suspended' ? (resumeBlocked ? resumeConfirm() : resumeFromSuspend()) : setUiMode('immersive')}
      onKeyDown={(e) => { if (e.key === 'Enter') setUiMode('immersive'); }}
      aria-label={t('voice.expand')}
    >
      <div className="voice-capsule-avatar"><Avatar size={36} /></div>
      <div className="voice-capsule-wave" aria-hidden="true"><span /><span /><span /></div>
      <div className="voice-capsule-time">{state === 'suspended' ? t('voice.state_suspended') : `${mm}:${ss}`}</div>
      <button className="voice-capsule-end" onClick={(e) => { e.stopPropagation(); endVoice(); }} aria-label={t('voice.end')}>
        <PhoneOff size={14} />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────
// 首次告知（V-COMP-01）
// ──────────────────────────────────────────────
// ──────────────────────────────────────────────
// 零助理引导（2026-08-17 定稿：无助理禁止开播，先引导创建）
// ──────────────────────────────────────────────
function NeedAssistantModal() {
  const { needAssistantOpen, setNeedAssistantOpen } = useVoice();
  const t = useT();
  const nav = useNavigate();
  if (!needAssistantOpen) return null;
  return (
    <div className="voice-modal-backdrop" role="dialog" aria-modal="true">
      <div className="voice-modal">
        <div className="voice-modal-title">{t('voice.need_assistant_title')}</div>
        <div className="voice-modal-body">{t('voice.need_assistant_body')}</div>
        <div className="voice-modal-actions">
          <button className="voice-btn-primary" onClick={() => { setNeedAssistantOpen(false); nav('/assistants'); }}>{t('voice.need_assistant_cta')}</button>
          <button className="voice-btn-ghost" onClick={() => setNeedAssistantOpen(false)}>{t('voice.consent_decline')}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 首次音色选择器（2026-08-17 定稿：按人设性别过滤，试听优雅降级）
// ──────────────────────────────────────────────
function VoicePickerModal() {
  const { pickerFor, pickerSkip, pickerChoose, pickerClose } = useVoice();
  const t = useT();
  const [voices, setVoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pendingNote, setPendingNote] = useState(null);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const previewsRef = useRef({});
  const { lang: uiLang } = useI18n(); // 语言切换时重拉目录（音色名 i18n）
  const catalogLang = uiLang === 'zh' ? 'zh' : 'en';
  const sampleLang = pickerFor && (pickerFor.lang === 'ja' || pickerFor.lang === 'zh' || pickerFor.lang === 'en')
    ? pickerFor.lang : catalogLang;

  useEffect(() => {
    if (!pickerFor) return undefined;
    setSelected(null); setPendingNote(null);
    let cancelled = false;
    api.get(`/voice/catalog?lang=${catalogLang}`).then(r => {
      if (cancelled) return;
      const all = r.data.voices || [];
      const filtered = pickerFor.gender ? all.filter(v => v.gender === pickerFor.gender) : all;
      const list = filtered.length ? filtered : all;
      setVoices(list);
      // 列表加载后立即预取试听，保证点击时 play() 处于同步调用栈内（Safari 自动播放策略）
      list.forEach(v => {
        api.get(`/voice/sample/${encodeURIComponent(v.id)}?lang=${sampleLang}`, {
          responseType: 'blob',
          skipErrorToast: true,
        }).then(res => {
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          const audio = new Audio(url);
          audio.preload = 'auto';
          previewsRef.current[v.id] = { audio, url };
        }).catch(() => {});
      });
    }).catch(() => setVoices([]));
    return () => {
      cancelled = true;
      try { audioRef.current && audioRef.current.pause(); } catch (_) {}
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      Object.keys(previewsRef.current).forEach(id => {
        try { previewsRef.current[id].audio.pause(); } catch (_) {}
        URL.revokeObjectURL(previewsRef.current[id].url);
        delete previewsRef.current[id];
      });
    };
  }, [pickerFor, uiLang, sampleLang]);

  useEffect(() => {
    if (!pickerFor) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') pickerClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickerFor, pickerClose]);

  if (!pickerFor) return null;

  const preview = (id) => {
    setPendingNote(null);
    const cached = previewsRef.current[id];
    if (!cached) {
      setPendingNote(id);
      return;
    }
    try { audioRef.current && audioRef.current.pause(); } catch (_) {}
    audioRef.current = cached.audio;
    blobUrlRef.current = cached.url;
    cached.audio.onerror = () => { setPendingNote(id); };
    cached.audio.onended = () => { setPendingNote(null); };
    try {
      cached.audio.currentTime = 0;
      const p = cached.audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => setPendingNote(id));
    } catch (_) { setPendingNote(id); }
  };

  return (
    <div className="voice-modal-backdrop" role="dialog" aria-modal="true">
      <div className="voice-modal">
        <div className="voice-modal-head">
          <div className="voice-modal-title">{t('voice.picker_title', { name: pickerFor.name })}</div>
          <button className="voice-modal-close" onClick={pickerClose} title={t('common.close')} aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>
        <div className="voice-modal-body">{t('voice.picker_desc')}</div>
        <div className="voice-picker-list">
          {voices.map(v => (
            <div key={v.id} className={`voice-picker-row ${selected === v.id ? 'selected' : ''}`} onClick={() => { setSelected(v.id); preview(v.id); }}>
              <span className="voice-picker-name">{v.label}</span>
            </div>
          ))}
        </div>
        {pendingNote && <div className="voice-picker-note">{t('voice.picker_preview_pending')}</div>}
        <div className="voice-modal-actions">
          <button className="voice-btn-ghost" disabled={!selected} onClick={() => selected && preview(selected)}>{t('voice.picker_preview')}</button>
          <button className="voice-btn-primary" disabled={!selected} onClick={() => pickerChoose(selected)}>{t('voice.picker_choose')}</button>
          <button className="voice-btn-ghost" onClick={pickerSkip}>{t('voice.picker_skip')}</button>
        </div>
      </div>
    </div>
  );
}

function ConsentModal() {
  const { consentOpen, acceptConsent, setConsentOpen, endVoice } = useVoice();
  const t = useT();
  if (!consentOpen) return null;
  return (
    <div className="voice-modal-backdrop" role="dialog" aria-modal="true">
      <div className="voice-modal">
        <div className="voice-modal-title">{t('voice.consent_title')}</div>
        <div className="voice-modal-body">{t('voice.consent_body')}</div>
        <div className="voice-modal-actions">
          <button className="voice-btn-primary" onClick={acceptConsent}>{t('voice.consent_accept')}</button>
          <button className="voice-btn-ghost" onClick={() => { setConsentOpen(false); endVoice(); }}>{t('voice.consent_decline')}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 达量弹窗（V-UI-16：情绪优先 + 三出口；话术 4.5 节定稿由服务端口播，此处是书面版）
// ──────────────────────────────────────────────
function QuotaModal() {
  const { quotaModalOpen, setQuotaModalOpen, quota } = useVoice();
  const t = useT();
  const nav = useNavigate();
  if (!quotaModalOpen) return null;
  const dateStr = quota?.resumeDate ? new Date(quota.resumeDate).toLocaleDateString() : '';
  return (
    <div className="voice-modal-backdrop" role="dialog" aria-modal="true">
      <div className="voice-modal voice-modal-quota">
        <div className="voice-quota-avatar"><Avatar size={96} /></div>
        <div className="voice-modal-body voice-quota-text">{t('voice.quota_exhausted_body', { date: dateStr })}</div>
        <div className="voice-modal-actions voice-modal-actions-col">
          <button className="voice-btn-primary" onClick={() => { setQuotaModalOpen(false); nav('/settings?tab=voice'); }}>
            {t('voice.quota_cta_pack')}
          </button>
          <button className="voice-btn-ghost" onClick={() => { setQuotaModalOpen(false); nav('/conversations'); }}>
            {t('voice.quota_cta_text')}
          </button>
          {dateStr && <div className="voice-quota-resume">{t('voice.quota_resume_note', { date: dateStr })}</div>}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 聚光灯（V-UI-03：目标光环脉冲 + 其余压暗；3-5s 淡出；同屏一个）
// ──────────────────────────────────────────────
function SpotlightOverlay() {
  const { spotlight, setSpotlight } = useVoice();
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const onSpot = (e) => setSpotlight(e.detail);
    window.addEventListener('voice:spotlight', onSpot);
    return () => window.removeEventListener('voice:spotlight', onSpot);
  }, [setSpotlight]);

  useEffect(() => {
    if (!spotlight) { setRect(null); return undefined; }
    setRect(null);
    let trackTimer = null;
    let landed = false;
    let anchorEl = null;
    const find = () => document.querySelector(`[data-voice-anchor="${spotlight.anchor}"]`);
    // F1'（2026-08-23）：原位持续点亮——发光施加于锚点元素自身（class 注入，天然免疫布局漂移），
    // 提示条浮动于元素旁（小浮签，非全屏、不暗化、不拦截页面交互）；无自动消失——
    // 仅显式用户动作（提示条关闭/ESC）或下一次聚光行动解除。效果解除后用户仍停落于
    // 正确页面与滚动位置（scroll-into-view），定位不丢失。
    const dismiss = () => {
      if (anchorEl && anchorEl.isConnected) anchorEl.classList.remove('voice-anchor-glow');
      setSpotlight(null);
    };
    const measure = () => {
      if (!anchorEl || !anchorEl.isConnected) { dismiss(); return; }
      const r = anchorEl.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const cx = Math.min(Math.max(r.left + r.width / 2, 120), vw - 120);
      const top = Math.max(8, r.top - 46);
      setRect({ top, left: cx });
    };
    const landOn = (el) => {
      if (landed) return;
      landed = true;
      anchorEl = el;
      el.classList.add('voice-anchor-glow');
      measure();
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      trackTimer = setInterval(measure, 500);
    };
    // 同页高亮目标通常已挂载；跨页 guide_to 需轮询等待路由页面渲染
    const first = find();
    if (first) landOn(first);
    const poll = setInterval(() => { const el = find(); if (el) { clearInterval(poll); landOn(el); } }, 200);
    // 10s 未找到 → 静默降级为纯口播，不黑屏不报错（2026-08-18：跨页路由渲染放宽）
    const deadline = setTimeout(() => { clearInterval(poll); if (!landed) setSpotlight(null); }, 10000);
    // 显式解除（显式用户动作；非计时）
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => {
      clearInterval(poll);
      clearInterval(trackTimer);
      clearTimeout(deadline);
      window.removeEventListener('keydown', onKey);
      if (anchorEl && anchorEl.isConnected) anchorEl.classList.remove('voice-anchor-glow');
    };
  }, [spotlight, setSpotlight]);

  if (!spotlight || !rect) return null;
  // F1'（2026-08-23）：邻近浮动提示条（无暗化层、无全屏拦截）；元素侧发光由 class 注入承担
  return (
    <div className="voice-spotlight-hint" style={{ top: rect.top, left: rect.left }}>
      <span>{spotlight.hint || '相关功能已高亮'}</span>
      <button type="button" className="voice-spotlight-hint-close" aria-label="close" onClick={() => setSpotlight(null)}>×</button>
    </div>
  );
}

// ──────────────────────────────────────────────
// 单会话冲突（V-ARCH-07：提示 + 接管按钮）
// ──────────────────────────────────────────────
function ConflictModal() {
  const { conflict, takeover, setConflict } = useVoice();
  const t = useT();
  if (!conflict) return null;
  return (
    <div className="voice-modal-backdrop" role="dialog" aria-modal="true">
      <div className="voice-modal">
        <div className="voice-modal-title">{t('voice.conflict_title')}</div>
        <div className="voice-modal-body">{t('voice.conflict_body')}</div>
        <div className="voice-modal-actions">
          <button className="voice-btn-primary" onClick={takeover}>{t('voice.conflict_takeover')}</button>
          <button className="voice-btn-ghost" onClick={() => setConflict(null)}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// 麦克风被拒引导（V-EDGE-07）
// ──────────────────────────────────────────────
function MicErrorToast() {
  const { micError, setMicError } = useVoice();
  const t = useT();
  if (!micError) return null;
  const msg = micError === 'denied' ? t('voice.mic_denied')
    : micError === 'insecure' ? t('voice.mic_insecure')
    : micError === 'notfound' ? t('voice.mic_notfound')
    : micError === 'busy' ? t('voice.mic_busy')
    : t('voice.mic_error');
  return (
    <div className="voice-toast" role="alert">
      <span>{msg}</span>
      <button onClick={() => setMicError(null)} aria-label={t('common.close')}><X size={14} /></button>
    </div>
  );
}

// ──────────────────────────────────────────────
// 刷新恢复条（V-EDGE-02）
// ──────────────────────────────────────────────
function ResumeBanner() {
  const { resumeHint, setResumeHint, startVoice, state } = useVoice();
  const t = useT();
  if (!resumeHint || state !== 'off') return null;
  return (
    <div className="voice-resume-banner">
      <span>{t('voice.resume_banner')}</span>
      <button className="voice-btn-primary voice-btn-sm" onClick={() => { const a = resumeHint.assistantId; setResumeHint(null); startVoice({ reattach: true, assistantId: a }); }}>
        {t('voice.resume_banner_cta')}
      </button>
      <button className="voice-btn-ghost voice-btn-sm" onClick={() => setResumeHint(null)}>{t('common.dismiss')}</button>
    </div>
  );
}

// ──────────────────────────────────────────────
// 确认门控（2026-08-23 Tier-2）：不可逆/涉资代操作的二次通道确认（非语音歧义）
// ──────────────────────────────────────────────
function OpConfirmBar() {
  const { opConfirm, setOpConfirm, sendAck, lang } = useVoice();
  const confirmedRef = useRef(false);
  useEffect(() => {
    confirmedRef.current = false;
  }, [opConfirm]);
  useEffect(() => {
    if (!opConfirm) return undefined;
    // 2026-08-23（R4 类泛化）：确认条有界过期——确认是用户亲手决策，但挂起态不得无限悬置；
    // 60s 无确认 → 自动取消（审计 denied:timeout），操作永不静默执行。
    const timer = setTimeout(() => {
      if (!confirmedRef.current) {
        sendAck && sendAck(opConfirm.callId, false, 'denied:timeout', 'operate');
        setOpConfirm(null);
      }
    }, 60000);
    return () => clearTimeout(timer);
  }, [opConfirm, setOpConfirm, sendAck]);
  if (!opConfirm) return null;
  const zh = lang !== 'en';
  const confirm = () => {
    const { callId, op } = opConfirm;
    confirmedRef.current = true;
    window.dispatchEvent(new CustomEvent('voice:operate', { detail: { op } }));
    sendAck && sendAck(callId, true, null, 'operate');
    setOpConfirm(null);
  };
  const cancel = () => {
    const { callId } = opConfirm;
    confirmedRef.current = true;
    sendAck && sendAck(callId, false, 'denied', 'operate');
    setOpConfirm(null);
  };
  return (
    <div className="voice-op-confirm">
      <span className="voice-op-confirm-text">
        {zh ? `即将执行：${opConfirm.hint || '该操作'} —— 由你亲手确认` : `About to perform: ${opConfirm.hint || 'this operation'} — your confirmation required`}
      </span>
      <button type="button" className="voice-op-confirm-yes" onClick={confirm}>{zh ? '确认' : 'Confirm'}</button>
      <button type="button" className="voice-op-confirm-no" onClick={cancel}>{zh ? '取消' : 'Cancel'}</button>
    </div>
  );
}

// ──────────────────────────────────────────────
// 全局语音开关 + 麦克风入口（V-UI-08/09）
// ──────────────────────────────────────────────
export function VoiceDock() {
  const { featureEnabled, voiceOn, setVoiceOn, state, startVoice } = useVoice();
  const t = useT();
  if (!featureEnabled) return null;
  return (
    <div className="voice-dock">
      <button
        className={`voice-toggle ${voiceOn ? 'on' : ''}`}
        onClick={() => setVoiceOn(!voiceOn)}
        title={t('voice.toggle_hint')}
        aria-label={t('voice.toggle_hint')}
        aria-pressed={voiceOn}
      >
        {voiceOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
      </button>
      {voiceOn && state === 'off' && (
        <button className="voice-mic-entry" onClick={() => startVoice()} title={t('voice.mic_hint')} aria-label={t('voice.mic_hint')}>
          <Mic size={17} />
        </button>
      )}
      {voiceOn && state !== 'off' && (
        <button className="voice-mic-entry live" aria-label={t('voice.state_active')}><Mic size={17} /></button>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// 面板总装（Layout 挂载一次）
// ──────────────────────────────────────────────
export function VoiceLayer() {
  const { state, uiMode, featureEnabled, voiceOn } = useVoice();
  const active = state !== 'off';
  return (
    <>
      {featureEnabled && voiceOn && active && uiMode === 'immersive' && <ImmersivePanel />}
      {featureEnabled && voiceOn && active && uiMode === 'capsule' && <FloatingCapsule />}
      {featureEnabled && voiceOn && active && uiMode === 'hidden' && <HiddenIndicator />}
      <ConsentModal />
      <NeedAssistantModal />
      <VoicePickerModal />
      <QuotaModal />
      <ConflictModal />
      <OpConfirmBar />
      <MicErrorToast />
      <SpotlightOverlay />
      <ResumeBanner />
    </>
  );
}

// 关框态：会话继续的极简指示（V-UI-07 关框≠挂断）
function HiddenIndicator() {
  const { setUiMode } = useVoice();
  const t = useT();
  return (
    <button className="voice-hidden-dot" onClick={() => setUiMode('immersive')} title={t('voice.hidden_hint')} aria-label={t('voice.hidden_hint')}>
      <Mic size={14} />
    </button>
  );
}

export default VoiceLayer;
